class GeoRedirectPopup extends HTMLElement {
  constructor() {
    super();

    this.SEK_SITES = ['se', 'au', 'pl', 'kr', 'nz', 'ca', 'uk', 'dk', 'no'];
    this.EURO_SITES = ['de', 'eu'];
    this.US_SITES = ['us'];

    this.COUNTRY_NAMES = {
      'se': 'Swedish',
      'au': 'Australian',
      'pl': 'Polish',
      'kr': 'Korean',
      'nz': 'New Zealand',
      'ca': 'Canadian',
      'uk': 'UK',
      'dk': 'Danish',
      'no': 'Norwegian',
      'de': 'German',
      'eu': 'European',
      'us': 'US',
      'int': 'International'
    };

    this.addEventListener('click', (evt) => {
      if (evt.target.closest('[data-geo-popup-close]')) this.close();
      if (evt.target.closest('[data-geo-popup-redirect]')) this.redirect();
    });
  }

  connectedCallback() {
    if (Shopify.designMode) return; 
    if (this.isBot()) return;
    if (this.shouldSkipRedirect()) return;
    if (this.hasShownInSession()) return;
    this.init();
  }

  isBot() {
    const botPatterns = [
      'googlebot', 'google-inspectiontool', 'storebot-google',
      'bingbot', 'slurp', 'duckduckbot', 'baiduspider',
      'yandexbot', 'facebookexternalhit', 'facebot', 'twitterbot',
      'linkedinbot', 'pinterest', 'semrushbot', 'ahrefsbot',
      'applebot', 'petalbot', 'mj12bot', 'dotbot'
    ];
    const ua = navigator.userAgent.toLowerCase();
    return botPatterns.some(bot => ua.includes(bot));
  }

  shouldSkipRedirect() {
    const params = new URLSearchParams(window.location.search);

    if (params.has('noredirect')) {
      sessionStorage.setItem('geo_noredirect', 'true');
      return true;
    }

    return sessionStorage.getItem('geo_noredirect') === 'true';
  }

  getRedirectedFromParam() {
    const params = new URLSearchParams(window.location.search);
    return params.get('redirected_from');
  }

  async init() {
    try {
      const redirectedFrom = this.getRedirectedFromParam();

      if (redirectedFrom) {
        this.handleRedirectedState(redirectedFrom);
        return;
      }

      const response = await fetch('/browsing_context_suggestions.json');
      const data = await response.json();

      const detectedCountry = this.getCountryFromData(data);
      const currentSite = this.getCurrentSite();
      const isMainSwedishSite = this.isMainSwedishSite();

      if (isMainSwedishSite) {
        this.handleMainSiteRedirect(detectedCountry);
      } else {
        this.handleCountrySitePopup(currentSite, detectedCountry);
      }
    } catch (error) {
      console.error('Geo redirect popup error:', error);
    }
  }

  handleRedirectedState(redirectedFrom) {
    const currentSite = this.getCurrentSite();
    const currentName = this.COUNTRY_NAMES[currentSite] || currentSite.toUpperCase();
    const originName = this.COUNTRY_NAMES[redirectedFrom] || redirectedFrom.toUpperCase();

    this.targetUrl = this.buildRedirectUrl(redirectedFrom, true);

    this.renderRedirectedPopup(currentName, originName);
    this.open();
  }

  handleMainSiteRedirect(detectedCountry) {
    if (!detectedCountry) return;

    if (detectedCountry === 'se') return;

    const targetSite = this.getTargetPrefix(detectedCountry);

    if (targetSite === 'se') return;

    const targetUrl = this.buildRedirectUrl(targetSite, false, 'se', detectedCountry);
    window.location.href = targetUrl;
  }

  handleCountrySitePopup(currentSite, detectedCountry) {
    const result = this.checkRedirect(currentSite, detectedCountry);

    if (result) {
      this.renderSuggestionPopup(result.currentSite, result.targetSite, result.targetUrl);
      this.open();
    }
  }

  getCountryFromData(data) {
    if (data && data.detected_values && data.detected_values.country) {
      return data.detected_values.country.handle.toLowerCase();
    }
    return null;
  }

  getCurrentSite() {
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];

    const allSites = [...this.SEK_SITES, ...this.EURO_SITES, ...this.US_SITES, 'b2b', 'int'];

    if (allSites.includes(subdomain)) {
      return subdomain;
    }

    return 'se';
  }

  isMainSwedishSite() {
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];
    const allSites = [...this.SEK_SITES, ...this.EURO_SITES, ...this.US_SITES, 'b2b', 'int'];

    return !allSites.includes(subdomain);
  }

  getTargetPrefix(country) {
    const countryToSite = { 'gb': 'uk' };
    const mapped = countryToSite[country] || country;

    if (this.SEK_SITES.includes(mapped)) return mapped;
    if (this.EURO_SITES.includes(mapped)) return mapped;
    if (this.US_SITES.includes(mapped)) return mapped;
    return 'int';
  }

  checkRedirect(currentSite, country) {
    if (!country) return null;

    if (currentSite === 'b2b') {
      return null;
    }

    const targetSite = this.getTargetPrefix(country);

    if (currentSite === targetSite) {
      return null;
    }

    const targetUrl = this.buildRedirectUrl(targetSite);

    return {
      currentSite: currentSite,
      targetSite: targetSite,
      targetUrl: targetUrl
    };
  }

  buildRedirectUrl(targetSite, addNoRedirect = false, redirectedFrom = null, country = null) {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    let baseDomain;
    if (parts.length >= 2) {
      baseDomain = parts.slice(-2).join('.');
    } else {
      baseDomain = hostname;
    }

    const currentParams = new URLSearchParams(window.location.search);
    currentParams.delete('redirected_from');
    currentParams.delete('noredirect');

    if (redirectedFrom) {
      currentParams.set('redirected_from', redirectedFrom);
    }

    if (country) {
      currentParams.set('country', country.toUpperCase());
    }

    let queryString = currentParams.toString();

    if (addNoRedirect) {
      queryString = queryString ? `${queryString}&noredirect` : 'noredirect';
    }

    const query = queryString ? `?${queryString}` : '';

    if (targetSite === 'se') {
      return `https://${baseDomain}${window.location.pathname}${query}`;
    }

    return `https://${targetSite}.${baseDomain}${window.location.pathname}${query}`;
  }

  renderSuggestionPopup(currentSite, targetSite, targetUrl) {
    const currentName = this.COUNTRY_NAMES[currentSite] || currentSite.toUpperCase();
    const targetName = this.COUNTRY_NAMES[targetSite] || targetSite.toUpperCase();

    this.targetUrl = targetUrl;

    const titleEl = this.querySelector('[data-geo-popup-title]');
    const continueBtn = this.querySelector('[data-geo-popup-continue]');
    const redirectBtn = this.querySelector('[data-geo-popup-redirect]');

    if (titleEl) {
      titleEl.textContent = `You are on the ${currentName} site`;
    }
    if (continueBtn) {
      continueBtn.textContent = `Continue on the ${currentName} site`;
    }
    if (redirectBtn) {
      redirectBtn.textContent = `To the ${targetName} site`;
    }
  }

  renderRedirectedPopup(currentName, originName) {
    const titleEl = this.querySelector('[data-geo-popup-title]');
    const continueBtn = this.querySelector('[data-geo-popup-continue]');
    const redirectBtn = this.querySelector('[data-geo-popup-redirect]');

    if (titleEl) {
      titleEl.textContent = `We redirected you to the ${currentName} store for better shipping and pricing`;
    }
    if (continueBtn) {
      continueBtn.textContent = 'Continue shopping';
    }
    if (redirectBtn) {
      redirectBtn.textContent = `Return to the ${originName} store`;
    }
  }

  hasShownInSession() {
    return sessionStorage.getItem('geo_redirect_popup_shown') === 'true';
  }

  markAsShown() {
    sessionStorage.setItem('geo_redirect_popup_shown', 'true');
  }

  open() {
    this.setAttribute('open', '');
    document.body.classList.add('overflow-hidden-geo-popup');
  }

  close() {
    this.removeAttribute('open');
    document.body.classList.remove('overflow-hidden-geo-popup');
    this.markAsShown();
  }

  redirect() {
    this.markAsShown();
    if (this.targetUrl) {
      window.location.href = this.targetUrl;
    }
  }
}

customElements.define('geo-redirect-popup', GeoRedirectPopup);
